// ABOUTME: Full article detail page with content display and related articles
// ABOUTME: Supports HTML content rendering, tag filtering, and engagement tracking

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Chip,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
} from '@mui/material';
import { Home, Visibility, CalendarToday } from '@mui/icons-material';
import { getKBArticle, getKBVideo, getRelatedArticles } from '../../services/api';
import { KBArticleDto, KBArticleListDto } from '../../types/knowledgeBase';
import { sanitizeKBHtml, isValidYouTubeUrl, isValidVimeoUrl, isValidS3Url } from '../../utils/sanitize';
import { errorTint } from '../../theme/tokens';

const ArticleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<KBArticleDto | null>(null);
  const [parsedContent, setParsedContent] = useState<string>('');
  const [relatedArticles, setRelatedArticles] = useState<KBArticleListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse video shortcodes [video:123] and replace with embedded players
  const parseVideoShortcodes = async (content: string): Promise<string> => {
    const videoShortcodeRegex = /\[video:(\d+)\]/g;
    const matches = Array.from(content.matchAll(videoShortcodeRegex));

    let parsedContent = content;

    for (const match of matches) {
      const videoId = parseInt(match[1]);
      try {
        const video = await getKBVideo(videoId);
        const playbackUrl = video.playbackUrl;

        const invalidUrlPlaceholder = `<div style="padding: 20px; background: ${errorTint[50]}; border: 1px solid ${errorTint[200]}; border-radius: 4px; margin: 20px 0;"><strong>Video unavailable (ID: ${videoId})</strong></div>`;

        let embedHtml = '';

        if (isValidYouTubeUrl(playbackUrl)) {
          const youtubeId = playbackUrl.split('v=')[1]?.split('&')[0] || playbackUrl.split('/').pop();
          embedHtml = `
            <div style="position: relative; padding-bottom: 56.25%; height: 0; margin: 20px 0;">
              <iframe
                src="https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&rel=0&modestbranding=1&showinfo=0"
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 8px;"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
            <p style="text-align: center; margin-top: -10px; margin-bottom: 20px;">
              <strong>${video.title}</strong>
            </p>
          `;
        } else if (isValidVimeoUrl(playbackUrl)) {
          const vimeoId = playbackUrl.split('/').pop();
          embedHtml = `
            <div style="position: relative; padding-bottom: 56.25%; height: 0; margin: 20px 0;">
              <iframe
                src="https://player.vimeo.com/video/${vimeoId}"
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 8px;"
                allow="autoplay; fullscreen; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
            <p style="text-align: center; margin-top: -10px; margin-bottom: 20px;">
              <strong>${video.title}</strong>
            </p>
          `;
        } else if (isValidS3Url(playbackUrl)) {
          // S3 or direct video URL
          embedHtml = `
            <div style="margin: 20px 0;">
              <video controls style="width: 100%; border-radius: 8px;">
                <source src="${playbackUrl}" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <p style="text-align: center; margin-top: 10px;">
                <strong>${video.title}</strong>
              </p>
            </div>
          `;
        } else {
          console.warn(`Video ${videoId}: blocked invalid playback URL "${playbackUrl}"`);
          embedHtml = invalidUrlPlaceholder;
        }

        parsedContent = parsedContent.replace(match[0], embedHtml);
      } catch (err) {
        console.error(`Failed to load video ${videoId}:`, err);
        // Replace with error message
        parsedContent = parsedContent.replace(
          match[0],
          `<div style="padding: 20px; background: ${errorTint[50]}; border: 1px solid ${errorTint[200]}; border-radius: 4px; margin: 20px 0;">
            <strong>Video not found (ID: ${videoId})</strong>
          </div>`
        );
      }
    }

    return parsedContent;
  };

  useEffect(() => {
    if (id) {
      loadArticle(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadArticle intentionally omitted; it is stable and adding it would cause extra fetches
  }, [id]);

  const loadArticle = async (articleId: number) => {
    try {
      setLoading(true);
      setError(null);
      const articleData = await getKBArticle(articleId);
      setArticle(articleData);

      // Parse video shortcodes in content, then sanitize to prevent stored XSS
      const contentWithVideos = await parseVideoShortcodes(articleData.content);
      setParsedContent(sanitizeKBHtml(contentWithVideos));

      // Load related articles (same category or similar tags)
      await loadRelatedArticles(articleData);
    } catch (err) {
      console.error('Failed to load article:', err);
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setError('Article not found or is not available.');
      } else {
        setError('Failed to load article. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedArticles = async (currentArticle: KBArticleDto) => {
    try {
      const related = await getRelatedArticles(currentArticle.id);
      setRelatedArticles(related);
    } catch (err) {
      console.error('Failed to load related articles:', err);
      // Non-critical, don't show error to user
    }
  };

  const handleTagClick = (tagName: string) => {
    // Navigate to knowledge base with tag filter
    navigate(`/knowledge-base?tag=${encodeURIComponent(tagName)}`);
  };

  const handleRelatedArticleClick = (articleId: number) => {
    navigate(`/knowledge-base/article/${articleId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Article not found'}</Alert>
        <Box mt={2}>
          <Link component="button" onClick={() => navigate('/knowledge-base')}>
            ← Back to Knowledge Base
          </Link>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Box mb={3}>
        <Breadcrumbs>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/knowledge-base')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Knowledge Base
          </Link>
          {article.categoryName && (
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate(`/knowledge-base?category=${article.categoryId}`)}
              sx={{ cursor: 'pointer' }}
            >
              {article.categoryName}
            </Link>
          )}
          <Typography variant="body2" color="text.primary">
            {article.title}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Article Header */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {article.title}
        </Typography>

        {article.description && (
          <Typography variant="h6" color="text.secondary" paragraph>
            {article.description}
          </Typography>
        )}

        {/* Metadata */}
        <Box display="flex" gap={3} alignItems="center" flexWrap="wrap" mb={2}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Visibility fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {article.viewCount} views
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <CalendarToday fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Updated {new Date(article.updatedAt).toLocaleDateString()}
            </Typography>
          </Box>

          {article.publishedAt && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Published {new Date(article.publishedAt).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tags */}
        {article.tags.length > 0 && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {article.tags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                onClick={() => handleTagClick(tag.name)}
                clickable
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Article Content */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box
          dangerouslySetInnerHTML={{ __html: parsedContent }}
          sx={{
            '& img': {
              maxWidth: '100%',
              height: 'auto',
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
          }}
        />
      </Paper>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <Box>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Related Articles
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {relatedArticles.map((related) => (
              <Box key={related.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' } }}>
                <Card>
                  <CardActionArea onClick={() => handleRelatedArticleClick(related.id)}>
                    {related.thumbnailUrl && (
                      <CardMedia
                        component="img"
                        height="140"
                        image={related.thumbnailUrl}
                        alt={related.title}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6" component="div" gutterBottom>
                        {related.title}
                      </Typography>
                      {related.description && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {related.description}
                        </Typography>
                      )}
                      <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                        {related.tagNames.slice(0, 2).map((tagName, idx) => (
                          <Chip key={idx} label={tagName} size="small" />
                        ))}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default ArticleDetailPage;
